/**
 * Profile Fallback Hierarchy
 * 
 * Implements a multi-tier fallback strategy for profile loading:
 * 1. get_profile_self RPC (primary)
 * 2. get_profile_secure RPC (secondary)
 * 3. Cached profile from localStorage
 * 4. Minimal profile from session metadata
 */

import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/auth';
import type { Session, PostgrestError } from '@supabase/supabase-js';
import { getValidCachedProfile, cacheProfile } from './profileCache';

export interface FallbackResult {
  profile: UserProfile | null;
  source: 'rpc_secure' | 'cache' | 'session' | 'error';
  shouldRetry: boolean;
  error?: string | Error;
}

/**
 * Build minimal profile from session metadata
 */
export function buildMinimalProfileFromSession(session: Session): UserProfile {
  return {
    id: session.user.id,
    user_id: session.user.id,
    email: session.user.email || '',
    full_name: session.user.user_metadata?.full_name || null,
    first_name: session.user.user_metadata?.first_name || null,
    last_name: session.user.user_metadata?.last_name || null,
    created_at: new Date().toISOString(),
  } as UserProfile;
}

/**
 * Determine if error is retryable (network/transient vs auth/permanent)
 */
function isRetryableError(error: PostgrestError | Error | null): boolean {
  const errorStatus =
    error && 'status' in error ? (error as { status?: number }).status : undefined;
  const errorCode =
    error && 'code' in error ? (error as { code?: string }).code : undefined;
  
  // Auth/access errors - don't retry
  if (errorStatus === 400 || errorStatus === 401 || errorStatus === 403) {
    return false;
  }
  
  if (error?.message?.includes('Access denied') || 
      error?.message?.includes('Authentication required')) {
    return false;
  }
  
  // Encryption errors - don't retry
  if (error?.message?.includes('Encryption key not configured') || 
      error?.message?.includes('decrypt') || 
      errorCode === '22P02' ||
      (errorCode === 'PGRST301' && error?.message?.includes('Encryption'))) {
    return false;
  }
  
  // No profile found - don't retry
  if (errorStatus === 406 || errorCode === 'PGRST116') {
    return false;
  }
  
  // Everything else is potentially retryable (network, 500s, etc)
  return true;
}

/**
 * Fetch profile with comprehensive fallback hierarchy
 */
export async function fetchProfileWithFallbacks(
  session: Session | null
): Promise<FallbackResult> {
  if (!session?.user?.id) {
    return {
      profile: null,
      source: 'error',
      shouldRetry: false,
      error: 'No session',
    };
  }

  const userId = session.user.id;

  // NOTE: get_profile_self can return a noisy 406 when no row exists.
  // For stability, we start with get_profile_secure first.

  // Priority 1: Try get_profile_secure RPC
  try {
    const { data, error } = await supabase
      .rpc('get_profile_secure', {
        target_user_id: userId,
        access_request_id: null,
      })
      .maybeSingle();

    if (!error && data) {
      console.debug('[ProfileFallback] ✅ Success via get_profile_secure');
      cacheProfile(data as UserProfile);
      return {
        profile: data as UserProfile,
        source: 'rpc_secure',
        shouldRetry: false,
      };
    }

    console.warn('[ProfileFallback] get_profile_secure error:', {
      code: error?.code,
      status:
        error && 'status' in error ? (error as { status?: number }).status : undefined,
    });

    if (!isRetryableError(error)) {
      throw error;
    }
  } catch (error) {
    console.debug('[ProfileFallback] ⚠️ get_profile_secure failed, trying cache');
  }

  // Priority 3: Try cached profile
  const cachedProfile = getValidCachedProfile(userId);
  if (cachedProfile) {
    console.debug('[ProfileFallback] ℹ️ Using cached profile (valid cache)');
    return {
      profile: cachedProfile,
      source: 'cache',
      shouldRetry: true, // Retry in background to refresh cache
    };
  }

  // Priority 4: Build minimal profile from session
  console.debug('[ProfileFallback] ℹ️ Using minimal session-based profile');
  const minimalProfile = buildMinimalProfileFromSession(session);
  
  return {
    profile: minimalProfile,
    source: 'session',
    shouldRetry: true, // Definitely want to retry to get full profile
  };
}

/**
 * Get user-friendly message for fallback source
 */
export function getFallbackMessage(source: FallbackResult['source']): string {
  switch (source) {
    case 'cache':
      return 'Using cached profile data';
    case 'session':
      return 'Using limited profile information from session';
    case 'error':
      return 'Unable to load profile';
    default:
      return '';
  }
}
